"use strict";
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.celoToSavings = exports.savingsToCELO = exports.SavingsKit = void 0;
const connect_1 = require("@celo/connect");
const bignumber_js_1 = __importDefault(require("bignumber.js"));
const SavingsCELO_1 = require("../types/web3-v1-contracts/SavingsCELO");
/**
 * SavingsKit provides wrappers to interact with SavingsCELO contract.
 * For operations that may not be exposed through wrappers, internal .contract object can
 * be used directly. See implementation of .deposit() wrapper as an example.
 */
class SavingsKit {
    constructor(kit, contractAddress) {
        this.kit = kit;
        this.contractAddress = contractAddress;
        this.deposit = () => {
            const txo = this.contract.methods.deposit();
            return (0, connect_1.toTransactionObject)(this.kit.connection, txo);
        };
        this.withdrawStart = async (savingsAmount) => {
            const lockedGold = await this.kit.contracts.getLockedGold();
            const election = await this.kit.contracts.getElection();
            const amt = new bignumber_js_1.default(savingsAmount).toString(10);
            const toUnlock = await this.contract.methods.savingsToCELO(amt).call();
            const nonvoting = await lockedGold.getAccountNonvotingLockedGold(this.contractAddress);
            const toRevoke = bignumber_js_1.default.maximum(nonvoting.negated().plus(toUnlock), 0);
            const votedGroups = await election.getGroupsVotedForByAccount(this.contractAddress);
            let pendingRevoke = {
                lesser: "0x0000000000000000000000000000000000000000",
                greater: "0x0000000000000000000000000000000000000000",
            };
            let activeRevoke = {
                lesser: "0x0000000000000000000000000000000000000000",
                greater: "0x0000000000000000000000000000000000000000",
            };
            if (votedGroups.length > 0) {
                const revokeGroup = votedGroups[votedGroups.length - 1];
                const votes = await election.getVotesForGroupByAccount(this.contractAddress, revokeGroup);
                const totalVotes = votes.pending.plus(votes.active);
                if (toRevoke.gt(totalVotes)) {
                    throw new Error(`Can not withdraw requested amount in a single transaction. ` +
                        `Current maximum withdrawable CELO in a single transaction is: ` +
                        `${nonvoting.plus(totalVotes).div(1e18).toFixed(18)} CELO!`);
                }
                const toRevokePending = bignumber_js_1.default.minimum(toRevoke, votes.pending);
                pendingRevoke = await election.findLesserAndGreaterAfterVote(votes.group, toRevokePending.negated());
                activeRevoke = await election.findLesserAndGreaterAfterVote(votes.group, toRevoke.negated());
            }
            const txo = this.contract.methods.withdrawStart(amt, pendingRevoke.lesser, pendingRevoke.greater, activeRevoke.lesser, activeRevoke.greater);
            return (0, connect_1.toTransactionObject)(this.kit.connection, txo);
        };
        this.pendingWithdrawals = async (account) => {
            const pendings = await this.contract.methods.pendingWithdrawals(account).call();
            return pendings[0].map((v, idx) => ({
                value: new bignumber_js_1.default(v),
                time: new bignumber_js_1.default(pendings[1][idx]),
            }));
        };
        this.withdrawFinish = async (pendings, index) => {
            const indexGlobal = await this.withdrawIndexGlobal(pendings, index);
            const txo = this.contract.methods.withdrawFinish(index, indexGlobal);
            return (0, connect_1.toTransactionObject)(this.kit.connection, txo);
        };
        this.withdrawCancel = async (pendings, index) => {
            const indexGlobal = await this.withdrawIndexGlobal(pendings, index);
            const txo = this.contract.methods.withdrawCancel(index, indexGlobal);
            return (0, connect_1.toTransactionObject)(this.kit.connection, txo);
        };
        this.withdrawIndexGlobal = async (pendings, index) => {
            const lockedGold = await this.kit.contracts.getLockedGold();
            const pendingsGlobal = await lockedGold.getPendingWithdrawals(this.contractAddress);
            const idx = pendingsGlobal.findIndex((p) => (p.value.eq(pendings[index].value) &&
                p.time.eq(pendings[index].time)));
            if (idx === -1) {
                throw Error(`{time: ${pendings[index].time}, value: ${pendings[index].value}} not found in pending withdrawals`);
            }
            return idx;
        };
        this.celoToSavings = async (celoAmount) => {
            const savingsAmount = await this.contract.methods.celoToSavings(new bignumber_js_1.default(celoAmount).toString(10)).call();
            return new bignumber_js_1.default(savingsAmount);
        };
        this.savingsToCELO = async (savingsAmount) => {
            const celoAmount = await this.contract.methods.savingsToCELO(new bignumber_js_1.default(savingsAmount).toString(10)).call();
            return new bignumber_js_1.default(celoAmount);
        };
        this.totalCELOSupply = async () => {
            const goldToken = await this.kit.contracts.getGoldToken();
            const lockedGold = await this.kit.contracts.getLockedGold();
            const balance = goldToken.balanceOf(this.contractAddress);
            const locked = lockedGold.getAccountTotalLockedGold(this.contractAddress);
            return (await balance).plus(await locked);
        };
        this.totalSavingsSupply = async () => {
            return new bignumber_js_1.default(await this.contract.methods.totalSupply().call());
        };
        this.totalSupplies = async () => {
            const celoTotal = this.totalCELOSupply();
            const savingsTotal = this.totalSavingsSupply();
            return {
                celoTotal: await celoTotal,
                savingsTotal: await savingsTotal,
            };
        };
        this.contract = new kit.web3.eth.Contract(SavingsCELO_1.ABI, contractAddress);
    }
}
exports.SavingsKit = SavingsKit;
const savingsToCELO = (savingsAmount, savingsTotal, celoTotal) => {
    if (savingsTotal.eq(0)) {
        return new bignumber_js_1.default(0);
    }
    return celoTotal.multipliedBy(savingsAmount).div(savingsTotal).integerValue(bignumber_js_1.default.ROUND_DOWN);
};
exports.savingsToCELO = savingsToCELO;
const celoToSavings = (celoAmount, celoTotal, savingsTotal) => {
    if (celoTotal.eq(0) || savingsTotal.eq(0)) {
        return new bignumber_js_1.default(celoAmount).multipliedBy(65536);
    }
    return savingsTotal.multipliedBy(celoAmount).div(celoTotal).integerValue(bignumber_js_1.default.ROUND_DOWN);
};
exports.celoToSavings = celoToSavings;
//# sourceMappingURL=savingskit.js.map